import { useEffect, useState } from "react";
import { Instagram, Facebook, Twitter, Music2, Mail, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { useSEO } from "@/components/Seo";
import { eyebrow } from "@/lib/styles";

export default function Contact() {
  useSEO(
    "Contact | Threads Unbound™",
    "Reach the Threads Unbound™ studio for commissions, wholesale, collaborations, or press."
  );
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const email = settings?.contact_email || "";
  const socials = [
    settings?.facebook_url && { href: settings.facebook_url, label: "Facebook", Icon: Facebook },
    settings?.instagram_url && { href: settings.instagram_url, label: "Instagram", Icon: Instagram },
    settings?.twitter_url && { href: settings.twitter_url, label: "Twitter", Icon: Twitter },
    settings?.tiktok_url && { href: settings.tiktok_url, label: "TikTok", Icon: Music2 },
  ].filter(Boolean);

  return (
    <main className="px-6 pb-32 pt-36 md:px-12 md:pt-44 lg:px-20" data-testid="contact-page">
      <MaskedLine>
        <span className={eyebrow}>The studio door</span>
      </MaskedLine>
      <h1 className="mt-6 font-serif leading-[0.95] tracking-tighter">
        <MaskedLine delay={0.15}>
          <span className="block text-5xl font-light sm:text-6xl lg:text-7xl">
            Say the <span className="italic text-[#D4AF37]">quiet thing</span>
          </span>
        </MaskedLine>
      </h1>
      <Reveal delay={0.3}>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60">
          Commissions, wholesale, collaborations, press. Or just a thread you want to pull on. We read everything.
        </p>
      </Reveal>

      <section className="mt-20 border-t border-white/10 pt-16" data-testid="contact-email-section">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.35em] text-white/30">Write to us</p>
          {email ? (
            <a
              href={`mailto:${email}`}
              data-testid="contact-email-link"
              className="group mt-4 inline-flex flex-wrap items-baseline gap-3 font-serif text-3xl font-light text-white/90 transition-colors duration-300 hover:text-[#D4AF37] sm:text-5xl"
            >
              {email}
              <ArrowUpRight className="h-6 w-6 self-center text-[#D4AF37] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </a>
          ) : (
            <p className="mt-4 font-serif text-3xl font-light text-white/30" data-testid="contact-email-pending">
              Email coming soon
            </p>
          )}
        </Reveal>
      </section>

      <section className="mt-20 border-t border-white/10 pt-16" data-testid="contact-socials-section">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.35em] text-white/30">Elsewhere</p>
          <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
            {socials.length > 0 ? (
              socials.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`contact-social-${label.toLowerCase()}`}
                  className="group flex items-center justify-between bg-[#0A0A0B] p-6 transition-colors duration-300 hover:bg-[#121214]"
                >
                  <span className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-[#D4AF37]" />
                    <span className="text-sm uppercase tracking-[0.2em] text-white/70 group-hover:text-white">
                      {label}
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-white/30 transition-colors duration-300 group-hover:text-[#D4AF37]" />
                </a>
              ))
            ) : (
              <p className="bg-[#0A0A0B] p-6 text-sm text-white/30" data-testid="contact-socials-pending">
                Social links coming soon.
              </p>
            )}
          </div>
        </Reveal>
      </section>
    </main>
  );
}
